/**
 * The browser's view of `GET /api/session/{session_id}` — the one endpoint
 * these pages call, and the contract the return page's poll is written
 * against.
 *
 * Ported from the monorepo's return-client.tsx / cancel-client.tsx, where the
 * same shape was declared twice. The response is deliberately narrow: the
 * route authenticates on the unguessable session id alone, so it returns only
 * status, the receipt JWS once issued, the (re-authorised) consumer return
 * URL, and the order id. Nothing else about the session is exposed to a
 * shopper's browser, and nothing else belongs in this type.
 *
 * Declared here rather than imported from `@worker/…` even though the alias
 * exists: this is the parse of an HTTP body, which is an external-data
 * boundary (testing-philosophy POLICY.md §2, §4) no matter who wrote the
 * server. A structural type shared with the Worker would let a
 * `res.json() as SessionStatus` cast stand in for the check below, and that
 * cast is precisely what would let a 200 carrying an error body flow into the
 * redirect builder as if it were a paid session.
 */

import {
  CHECKOUT_SESSION_STATUSES,
  type CheckoutSessionStatus,
} from "@bindery/shared/checkout/api";

/** Lifecycle of a checkout session, as the API reports it. */
export type SessionState = CheckoutSessionStatus;

export interface SessionStatus {
  readonly status: SessionState;
  /** The signed receipt (a JWS). Present only once the webhook has issued it. */
  readonly receipt?: string;
  /** The consumer app's return URL, re-authorised against the allow-list on
   *  every read — so it can be absent on a session that had one at creation. */
  readonly return_url?: string;
  /** Echoed back when the session knows it. */
  readonly order_id?: string;
}

const STATES: readonly string[] = CHECKOUT_SESSION_STATUSES;

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Narrow a decoded JSON body to `SessionStatus`, or `null` if it is not one.
 *
 * An unrecognised `status` yields `null` rather than a partial object: the
 * poll treats `null` as "nothing conclusive, keep waiting", which is the safe
 * reading of a body we do not understand. Silently defaulting an unknown
 * state to `created` would look identical right up until the API grows a
 * fifth state, at which point every shopper in it would poll for two minutes
 * and then be told to email support.
 */
export function parseSessionStatus(body: unknown): SessionStatus | null {
  if (typeof body !== "object" || body === null) return null;
  const record = body as Record<string, unknown>;
  if (!STATES.includes(record.status as string)) return null;

  return {
    status: record.status as SessionState,
    receipt: optionalString(record.receipt),
    return_url: optionalString(record.return_url),
    order_id: optionalString(record.order_id),
  };
}

/**
 * The endpoint, host-relative.
 *
 * The monorepo fetched `/checkout/api/session/{id}`; that `/checkout` prefix
 * was an artifact of its subdomain-rewrite proxy, which does not exist here —
 * this app IS checkout.sara.ai, and its Worker routes `/api/*` directly.
 * Nothing else about the request changed in the port.
 */
export function sessionEndpoint(sessionId: string): string {
  return `/api/session/${encodeURIComponent(sessionId)}`;
}

/** The request every caller of this endpoint makes: JSON, never cached. A
 *  cached status is a shopper stuck watching a stale "confirming". */
export const SESSION_REQUEST_INIT: RequestInit = {
  headers: { accept: "application/json" },
  cache: "no-store",
};
